import { Box, Typography } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { CurrentStateOutcomeStyles } from 'components/core/task/current-state/styles'
import { Outcome } from 'components/core/task/outcome'
import { TaskValue } from 'lib/actions/task'
import { UserContext } from 'lib/context/app'
import { ListChecks, Trash2, Upload } from 'lucide-react'
import { TaskState } from 'pbts/c1/api/task/v1/task'
import { FC, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

const CurrentStateOutcome: FC<{ icon: React.ReactElement; text: string | React.ReactElement }> = ({ icon, text }) => {
    return (
        <Box sx={CurrentStateOutcomeStyles.container}>
            {icon}
            <Typography sx={CurrentStateOutcomeStyles.textContainer}>
                {text}
            </Typography>
        </Box>
    )
}

const PendingApproval: FC<{ task: TaskValue }> = ({ task }: { task: TaskValue }) => {
    const userContext = useContext(UserContext)
    const userId = userContext.user.id
    if (task.deletedAt) {
        return (
            <CurrentStateOutcome
                icon={<C1Icon icon={Trash2} sx={CurrentStateOutcomeStyles.deletedIcon} />}
                text={
                    <FormattedMessage id="task_current_state_deleted" defaultMessage="Deleted" />
                }
            />
        )
    }
    return (
        <CurrentStateOutcome
            icon={<C1Icon icon={ListChecks} sx={CurrentStateOutcomeStyles.pendingIcon} />}
            text={
                task.stepApproverIds.includes(userId) ? (
                    <FormattedMessage id="task_current_state_awaiting_approval" defaultMessage="Awaiting approval" />
                ) : (
                    <FormattedMessage
                        id="task_current_state_awaiting_additional_approval"
                        defaultMessage="Awaiting further approval"
                    />
                )
            }
        />
    )
}

const Provisioning: FC<{ task: TaskValue; provisioningText: string; pendingText: string }> = ({
    task,
    provisioningText,
    pendingText,
}) => {
    const userContext = useContext(UserContext)
    const userId = userContext.user.id

    return (
        <CurrentStateOutcome
            icon={<C1Icon icon={Upload} sx={CurrentStateOutcomeStyles.provisioningIcon} />}
            text={task.stepApproverIds.includes(userId) ? pendingText : provisioningText}
        />
    )
}

const CertifyTicketCurrentState: FC<{ task: TaskValue }> = ({ task }) => {
    const currentStep = task.policy?.current?.instance

    if (currentStep?.oneofKind !== 'approval') {
        return null
    }

    return <PendingApproval task={task} />
}

const GrantTicketCurrentState: FC<{ task: TaskValue }> = ({ task }) => {
    const intl = useIntl()

    switch (task.policy?.current?.instance?.oneofKind) {
        case 'approval':
            return <PendingApproval task={task} />
        case 'provision':
            return (
                <Provisioning
                    task={task}
                    provisioningText={intl.formatMessage({
                        id: 'component_task_current_state_provisioning',
                        defaultMessage: 'Provisioning',
                    })}
                    pendingText={intl.formatMessage({
                        id: 'component_task_current_state_pending_provisioning',
                        defaultMessage: 'Awaiting provision',
                    })}
                />
            )
        default:
            return null
    }
}

const RevokeTicketCurrentState: FC<{ task: TaskValue }> = ({ task }) => {
    const intl = useIntl()

    switch (task.policy?.current?.instance?.oneofKind) {
        case 'approval':
            return <PendingApproval task={task} />
        case 'provision':
            return (
                <Provisioning
                    task={task}
                    provisioningText={intl.formatMessage({
                        id: 'component_task_current_state_deprovisioning',
                        defaultMessage: 'Deprovisioning',
                    })}
                    pendingText={intl.formatMessage({
                        id: 'component_task_current_state_pending_provisioning',
                        defaultMessage: 'Awaiting deprovision',
                    })}
                />
            )
        default:
            return null
    }
}

export const CurrentState: FC<{ task: TaskValue }> = ({ task }) => {
    if (task.state === TaskState.CLOSED) {
        return <Outcome task={task} showResolutionDate={false} />
    }

    switch (task.type?.taskType.oneofKind) {
        case 'certify':
            return <CertifyTicketCurrentState task={task} />
        case 'grant':
            return <GrantTicketCurrentState task={task} />
        case 'revoke':
            return <RevokeTicketCurrentState task={task} />
        default:
            return null
    }
}
